/**
 * @file AuthContext.js
 * @description Provides authentication state and functionality to child components.
 */

import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../services/userService';
import { getConfigData } from '../services/authService';
import { parseJwt, checkForCommonValue } from '../utils/utils';
import Spinner from '../components/ui/spinners/Spinner';
import { getCurrentTheme } from '../services/themeService';
import { PUBLIC_PATHS } from '../AppRoutes';
import Toast from '../components/ui/toasts/Toast';
import { UserType, ClientOptionEnum } from '../utils/data/enums';
import { getClientOptions } from '../services/clientService';
import { isMobileDevice } from '../utils/deviceCheck';
import { clearRequestCacheByNamespace } from '../utils/cache/requestCache';
import { ROUTE_BUILDERS } from '../constants/routes';
import { fetchSecurityAttributes } from '../services/securityRoleService';
import { fetchTenantTiers } from '../services/systemService';
import { isIndividualViewRoute } from '../utils/individualViewRoutes';
import {
    isSameCompanyCode,
    readLinkCompanyCode,
    removeLinkCompanyCode,
} from '../utils/externalAppLinks';
import {
    parseFieldNamingResponse,
    buildFieldNamingMap,
    buildDefaultFieldNamingMap,
} from '../utils/data/fieldNamingUtils';
import {
    DEFAULT_MENU_LABELS,
    buildMenuLabelsMap,
    parseDisplayIndividualAssetMenu,
} from '../utils/data/menuLabelUtils';
import { getText } from '../i18n';

const ADMIN_VIEW_STORAGE_KEY = 'adminView';

const REQUEST_CACHE_NAMESPACES = [
    'portal-pages-list',
    'portal-page-detail',
    'asset-by-id',
];

const clearAllRequestCaches = () => {
    REQUEST_CACHE_NAMESPACES.forEach(namespace => {
        clearRequestCacheByNamespace(namespace);
    });
};

const getStoredAdminView = () => {
    const storedValue = localStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
    if (storedValue === 'true') {
        return true;
    }
    if (storedValue === 'false') {
        return false;
    }
    return null;
};

const resolveInitialAdminView = jwtPayload => {
    const storedAdminView = getStoredAdminView();
    if (storedAdminView !== null) {
        return storedAdminView;
    }

    return jwtPayload?.type <= UserType.ClientAdmin;
};

const canUseAdminView = jwtPayload => jwtPayload?.type <= UserType.ClientAdmin;

const settledValue = (result, fallback) =>
    result.status === 'fulfilled' ? result.value : fallback;

/**
 * @typedef {Object} AuthContextValue
 * @property {boolean} isLoggedIn - Indicates whether the user is logged in or not.
 * @property {Object} user - The user object containing user information.
 * @property {Function} login - Function to log in the user.
 * @property {Function} logout - Function to log out the user.
 */
/**
 * @type {React.Context<AuthContextValue>}
 * @description The context for authentication-related data and methods.
 */
const AuthContext = createContext(null);
/**
 * AuthContextProvider - Provides authentication state and functionality to child components.
 */

const isExemptedPath = (currentPath, exemptedPaths) => {
    return exemptedPaths.some(pattern => {
        if (!pattern.includes('*')) {
            return pattern === currentPath;
        }
        const basePattern = pattern.replace('*', '');
        return currentPath.startsWith(basePattern);
    });
};

/**
 * Provides authentication context for the application.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @returns {ReactNode} The rendered component.
 */
const AuthContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [companyConfig, setCompanyConfig] = useState(null);
    const [publicBrand, setPublicBrand] = useState(null);
    const [companyTheme, setCompanyTheme] = useState(null);
    const [multitenantFlag, setMultitenantFlag] = useState(
        localStorage.getItem('multitenantFlag') === 'true',
    );
    const companyCode = localStorage.getItem('companyCode');
    const [adminView, setAdminViewState] = useState(() => getStoredAdminView());
    const [systemConfig, setSystemConfig] = useState({
        securityAttributes: [],
        tenantTiers: [],
    });
    const setAdminView = value => {
        setAdminViewState(value);
        if (value === null) {
            localStorage.removeItem(ADMIN_VIEW_STORAGE_KEY);
            return;
        }
        localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, String(value));
    };

    const updateMultitenantFlag = value => {
        setMultitenantFlag(value);
        localStorage.setItem('multitenantFlag', value);
    };

    const [allowAdditionalUser, setAllowAdditionalUser] = useState();
    const [fieldNamingMap, setFieldNamingMap] = useState({});
    const [defaultFieldNamingMap, setDefaultFieldNamingMap] = useState({});
    const [enableEmailAndPhoneValidation, setEnableEmailAndPhoneValidation] =
        useState();
    const [menuLabels, setMenuLabels] = useState(DEFAULT_MENU_LABELS);
    const [displayIndividualAssetMenu, setDisplayIndividualAssetMenu] =
        useState(false);
    const [menuLabelsLoaded, setMenuLabelsLoaded] = useState(false);
    const previousTenantIdRef = useRef(localStorage.getItem('tenantId'));

    useEffect(() => {
        const isAdditionalUser = Boolean(
            companyConfig?.application?.allow_additional_user &&
            (user?.jwt?.type === UserType.Individual ||
                user?.jwt?.type === UserType.ClientAdmin),
        );
        setAllowAdditionalUser(isAdditionalUser);
    }, [companyConfig, user]);

    useEffect(() => {
        const enforceAdminViewEligibility = async () => {
            if (!user?.jwt || adminView !== true) {
                return;
            }

            const isAllowedAdminView = canUseAdminView(user.jwt);
            if (!isAllowedAdminView) {
                setAdminView(false);
            }
        };

        enforceAdminViewEligibility();
    }, [user, adminView]);

    const login = (userData, companyConfig, companyTheme, systemConfig) => {
        try {
            const impToken = localStorage.getItem('impersonation_token');
            const accessToken =
                impToken ?? localStorage.getItem('access_token');
            const jwtPayload = parseJwt(accessToken);
            const { tenant = null } = jwtPayload;
            if (!localStorage.getItem('tenantId') && tenant)
                localStorage.setItem('tenantId', tenant);

            if (adminView === null) {
                const initialAdminView = resolveInitialAdminView(jwtPayload);
                setAdminView(initialAdminView);
            }

            setIsLoggedIn(true);
            setUser({ ...userData, jwt: jwtPayload });
            setCompanyConfig(companyConfig);
            setCompanyTheme(companyTheme);
            setSystemConfig(systemConfig);
        } catch (e) {
            console.log(e);
            logout();
        }
    };

    const NAV_SESSION_KEYS = [
        'navigationStack:back',
        'navigationStack:forward',
        'navigationStack:names',
    ];

    const clearNavigationSession = () => {
        try {
            NAV_SESSION_KEYS.forEach(key => {
                try {
                    sessionStorage.removeItem(key);
                } catch (innerErr) {
                    console.warn(
                        `Failed to remove sessionStorage key ${key}`,
                        innerErr,
                    );
                }
            });
        } catch (e) {
            console.warn('Failed to clear navigation session keys', e);
        }
    };

    /**
     * Logs out the user.
     * @param {boolean} isExpired - Indicates whether the logout is due to token expiration.
     */
    const logout = (isExpired = false, preserveForm = isExpired) => {
        setIsLoggedIn(false);
        setUser(null);
        setMultitenantFlag(false);
        setAdminView(false);
        localStorage.removeItem('access_token');
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('multitenantFlag');
        localStorage.removeItem('documentUrl');
        localStorage.removeItem(ADMIN_VIEW_STORAGE_KEY);
        clearAllRequestCaches();

        clearNavigationSession(); // Clear navigation-related session storage

        // A stale token shouldn't cost the user the page they asked for; a deliberate
        // sign-out should not bounce them back into it.
        navigateToLogin(isExpired, preserveForm);
    };

    useEffect(() => {
        const currentTenantId = localStorage.getItem('tenantId');
        if (previousTenantIdRef.current !== currentTenantId) {
            clearAllRequestCaches();
            previousTenantIdRef.current = currentTenantId;
        }
    }, [location.pathname]);

    useEffect(() => {
        const defaultTheme = {
            id: 0,
            primaryLogo: null,
            secondaryLogo: null,
            favicon: '/icons/icon-120.png',
            pageHeaderColor1: '#1366ff',
            pageHeaderColor2: '#17bdbf',
            accessoryColor: '#1366ff',
            accessoryTextColor: '#f8fafc',
            loginBackgroundColor: '#f8fafc',
            loginBackgroundImage: null,
        };

        const theme = {
            ...(companyConfig?.public_brand || {}),
            ...(companyTheme || defaultTheme),
        };
        setPublicBrand(theme);
    }, [companyConfig, companyTheme]);

    useEffect(() => {
        const initializeAuthContext = async () => {
            const impToken = localStorage.getItem('impersonation_token');
            const accessToken =
                impToken ?? localStorage.getItem('access_token');
            const companyCode = localStorage.getItem('companyCode');
            const storedMultitenantFlag =
                localStorage.getItem('multitenantFlag') === 'true';
            const linkCompanyCode = isExemptedPath(
                location.pathname,
                PUBLIC_PATHS,
            )
                ? null
                : readLinkCompanyCode(location.search);

            setMultitenantFlag(storedMultitenantFlag);

            // A session belongs to one client, so a link for another client must sign in again.
            if (
                accessToken &&
                linkCompanyCode &&
                !isSameCompanyCode(linkCompanyCode, companyCode)
            ) {
                logout(false, true);
                return;
            }

            if (accessToken && linkCompanyCode) {
                navigate(
                    `${location.pathname}${removeLinkCompanyCode(location.search)}${location.hash}`,
                    { replace: true, state: location.state },
                );
            }

            if (accessToken) {
                try {
                    const jwtPayload = parseJwt(accessToken);
                    const { tenant = null } = jwtPayload;
                    if (!localStorage.getItem('tenantId') && tenant)
                        localStorage.setItem('tenantId', tenant);

                    const storedAdminView = getStoredAdminView();
                    const isInMobileDevice = await isMobileDevice();
                    const isMultitenantUser = jwtPayload?.ind == null;
                    const requestedAdminView =
                        storedAdminView !== null
                            ? storedAdminView
                            : resolveInitialAdminView(jwtPayload) &&
                              (!isInMobileDevice || isMultitenantUser);
                    const isAllowedAdminView = canUseAdminView(jwtPayload);
                    // Landing directly on an Individual-only route resolves to Individual
                    // view up front, so the Client Admin shell never renders first.
                    const forcedIndividualView =
                        isIndividualViewRoute(window.location.pathname) &&
                        !isMultitenantUser;
                    const initialAdminView =
                        requestedAdminView &&
                        isAllowedAdminView &&
                        !forcedIndividualView;
                    setAdminView(initialAdminView);

                    let userResponse = null;
                    try {
                        userResponse = await getCurrentUser();
                    } catch (userError) {
                        const status = userError?.response?.status;
                        if (status === 401 || status === 403) {
                            logout(true);
                            return;
                        }
                        // A transient/network failure here must not sign the user
                        // out; keep the existing token and fall back gracefully.
                        console.error(
                            'Unable to fetch current user, retaining session:',
                            userError,
                        );
                    }

                    const [
                        companyConfigResult,
                        companyThemeResult,
                        securityAttributesResult,
                        tenantTiersResult,
                        defaultFieldNamingResult,
                        clientFieldNamingResult,
                        emailPhoneValidationResult,
                    ] = await Promise.allSettled([
                        getConfigData(companyCode),
                        getCurrentTheme(companyCode),
                        fetchSecurityAttributes(true, true),
                        fetchTenantTiers(),
                        getClientOptions(
                            0,
                            [ClientOptionEnum.FieldNaming],
                            true,
                        ),
                        getClientOptions(0, [ClientOptionEnum.FieldNaming]),
                        getClientOptions(0, [
                            ClientOptionEnum.EnableEmailAndPhoneValidation,
                        ]),
                    ]);

                    const defaultFieldNamingObject = parseFieldNamingResponse(
                        settledValue(defaultFieldNamingResult, []),
                    );
                    if (defaultFieldNamingObject?.value) {
                        const defaultJSON = JSON.parse(
                            defaultFieldNamingObject.value,
                        );
                        setDefaultFieldNamingMap(
                            buildDefaultFieldNamingMap(defaultJSON),
                        );
                    }

                    const fieldNamingObject = parseFieldNamingResponse(
                        settledValue(clientFieldNamingResult, []),
                    );
                    if (fieldNamingObject?.value) {
                        const fieldNamingJSON = JSON.parse(
                            fieldNamingObject.value,
                        );
                        setFieldNamingMap(buildFieldNamingMap(fieldNamingJSON));
                    }

                    const emailPhoneValidationOption = settledValue(
                        emailPhoneValidationResult,
                        [],
                    ).find(
                        item =>
                            item.clientOptionId ===
                            ClientOptionEnum.EnableEmailAndPhoneValidation,
                    );
                    if (emailPhoneValidationOption) {
                        setEnableEmailAndPhoneValidation(
                            emailPhoneValidationOption.value,
                        );
                    }

                    login(
                        { ...(userResponse || {}), jwt: jwtPayload },
                        settledValue(companyConfigResult, null),
                        settledValue(companyThemeResult, null),
                        {
                            securityAttributes: settledValue(
                                securityAttributesResult,
                                [],
                            ),
                            tenantTiers: settledValue(tenantTiersResult, []),
                        },
                    );
                } catch (error) {
                    console.error('Authentication verification failed:', error);
                    logout();
                }
            } else navigateToLogin();
        };

        initializeAuthContext().then(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setMenuLabelsLoaded(false);
            return;
        }

        let isCancelled = false;

        const loadMenuLabels = async () => {
            try {
                const menuLabelResponse = await getClientOptions(0, [
                    ClientOptionEnum.RenameMessagesMenu,
                    ClientOptionEnum.RenameBenefitsMenu,
                    ClientOptionEnum.RenameAssetsMenu,
                    ClientOptionEnum.DisplayIndividualAssetMenu,
                ]);
                if (isCancelled) return;
                setMenuLabels(buildMenuLabelsMap(menuLabelResponse));
                setDisplayIndividualAssetMenu(
                    parseDisplayIndividualAssetMenu(menuLabelResponse),
                );
            } catch (error) {
                console.error('Unable to load menu labels:', error);
            } finally {
                if (!isCancelled) setMenuLabelsLoaded(true);
            }
        };

        loadMenuLabels();

        return () => {
            isCancelled = true;
        };
    }, [isLoggedIn]);

    useEffect(() => {
        const handleStorageChange = () => {
            try {
                const stored =
                    localStorage.getItem('multitenantFlag') === 'true';
                setMultitenantFlag(stored);
                const currentTenantId = localStorage.getItem('tenantId');
                if (previousTenantIdRef.current !== currentTenantId) {
                    clearAllRequestCaches();
                    previousTenantIdRef.current = currentTenantId;
                }
            } catch (e) {
                console.error('Failed to handle storage event', e);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Tabs share one session, so a sign-out or another client's sign-in in a different tab invalidates this one.
    useEffect(() => {
        if (!user?.jwt) {
            return undefined;
        }

        const handleSessionReplaced = event => {
            if (event.key !== 'access_token') {
                return;
            }

            if (
                event.newValue &&
                parseJwt(event.newValue)?.tenant === user.jwt.tenant
            ) {
                return;
            }

            window.location.reload();
        };

        window.addEventListener('storage', handleSessionReplaced);
        return () =>
            window.removeEventListener('storage', handleSessionReplaced);
    }, [user]);

    const reloadCompanyTheme = async (companyCode = null) => {
        try {
            const updatedCompanyTheme = await getCurrentTheme(companyCode);
            setCompanyTheme(updatedCompanyTheme);
        } catch (error) {
            console.error('Unable to reload theme:', error);
            if (!isExemptedPath(location.pathname, PUBLIC_PATHS)) {
                Toast.error(getText('msg_5562aba0ee31'));
            }
        }
    };

    const reloadFieldNaming = async () => {
        try {
            const [defaultResponse, clientResponse] = await Promise.all([
                getClientOptions(0, [ClientOptionEnum.FieldNaming], true),
                getClientOptions(0, [ClientOptionEnum.FieldNaming]),
            ]);

            const defaultFieldNamingObject =
                parseFieldNamingResponse(defaultResponse);
            if (defaultFieldNamingObject?.value) {
                const defaultJSON = JSON.parse(defaultFieldNamingObject.value);
                setDefaultFieldNamingMap(
                    buildDefaultFieldNamingMap(defaultJSON),
                );
            }

            const fieldNamingObject = parseFieldNamingResponse(clientResponse);
            if (fieldNamingObject?.value) {
                const fieldNamingJSON = JSON.parse(fieldNamingObject.value);
                setFieldNamingMap(buildFieldNamingMap(fieldNamingJSON));
            }
        } catch (error) {
            console.error('Unable to reload field naming:', error);
        }
    };

    const reloadMenuLabels = async () => {
        try {
            const menuLabelResponse = await getClientOptions(0, [
                ClientOptionEnum.RenameMessagesMenu,
                ClientOptionEnum.RenameBenefitsMenu,
                ClientOptionEnum.RenameAssetsMenu,
                ClientOptionEnum.DisplayIndividualAssetMenu,
            ]);
            setMenuLabels(buildMenuLabelsMap(menuLabelResponse));
            setDisplayIndividualAssetMenu(
                parseDisplayIndividualAssetMenu(menuLabelResponse),
            );
        } catch (error) {
            console.error('Unable to reload menu labels:', error);
        }
    };

    /**
     * Navigates to the login page with optional query parameters.
     *
     * @param {boolean} [isExpired=false] - Indicates if the authentication is expired.
     * @param {boolean} [preserveFrom=true] - Indicates if current route should be used as post-login redirect target.
     */
    const navigateToLogin = (isExpired = false, preserveFrom = true) => {
        if (!isExemptedPath(location.pathname, PUBLIC_PATHS)) {
            const companyCode = localStorage.getItem('companyCode');
            const authCompanyCode =
                localStorage.getItem('authCompanyCode') || '';
            const linkCompanyCode = readLinkCompanyCode(location.search);
            const searchParams = new URLSearchParams(location.search);

            if (isExpired) {
                localStorage.setItem('isAuthExpired', 'true');
            }

            if (linkCompanyCode || authCompanyCode || companyCode) {
                searchParams.set(
                    'companyCode',
                    linkCompanyCode || authCompanyCode || companyCode,
                );

                if (authCompanyCode) {
                    localStorage.removeItem('authCompanyCode');
                }
            }

            const searchString = searchParams.toString();
            const nextState = preserveFrom
                ? {
                      from: {
                          pathname: location.pathname,
                          search: removeLinkCompanyCode(location.search),
                          hash: location.hash,
                      },
                  }
                : {};

            navigate(ROUTE_BUILDERS.loginWithRawQueryString(searchString), {
                replace: true,
                state: nextState,
            });
        }
    };

    const getTenantTierLevel = tenantTierId => {
        if (systemConfig?.tenantTiers?.length === 0) {
            console.warn('Tenant tiers not loaded in system configuration');
            return 0;
        }

        var tenantTier = systemConfig.tenantTiers.find(
            tier => tier.id === tenantTierId,
        );
        if (!tenantTier) {
            console.error(
                `Tenant tier with ID ${tenantTierId} not found in system configuration`,
            );
            return 0;
        }

        return tenantTier.level;
    };

    /**
     * @param {int} minimumRequiredTenantTierId - the minimum required tier ID
     * @param {int|null} overrideTenantTierId - optional tenant tier ID to use instead of the one from the company config. Primarily used on the multitenant side
     * @returns {boolean} - true if the tenant's tier meets or exceeds the required minimum, false otherwise
     */
    const clientTierMeetsRequirement = (
        minimumRequiredTenantTierId,
        overrideTenantTierId = null,
    ) => {
        if ((minimumRequiredTenantTierId ?? 0) === 0) return true;

        const tenantTierId =
            overrideTenantTierId ?? companyConfig?.tenant_tier_id;

        if (isNaN(tenantTierId) || isNaN(minimumRequiredTenantTierId))
            return false;

        return (
            getTenantTierLevel(tenantTierId) >=
            getTenantTierLevel(minimumRequiredTenantTierId)
        );
    };

    const hasAttribute = securityAttribute => {
        return hasSecurityAttribute([securityAttribute]);
    };

    const hasSecurityAttribute = securityAttributeIds => {
        // user doesn't have any permissions
        if (!user.jwt.perms) return false;

        // mulittenant flag is on so we allow all permisions that the user has without checking tenant tier requirements
        if (multitenantFlag)
            return checkForCommonValue(securityAttributeIds, user.jwt.perms);

        // Muilti tenant users will often have permissions that not valid for the client
        // Check if the tenant tier meets the requirement for each attribute and only include those that meet the requirement
        const validAttributesForTier = user.jwt.perms.filter(attr => {
            const securityAttribute = systemConfig.securityAttributes.find(
                a => a.id === attr,
            );
            return clientTierMeetsRequirement(
                securityAttribute?.minimumRequiredTenantTierId,
            );
        });

        return checkForCommonValue(
            securityAttributeIds,
            validAttributesForTier,
        );
    };

    const isSelfRegistrationEnabled =
        companyConfig?.application?.self_registration === true;

    const getFieldLabel = useCallback(
        defaultLabel => {
            const mappingKey = defaultFieldNamingMap?.[defaultLabel];
            if (
                mappingKey &&
                fieldNamingMap?.[mappingKey] &&
                fieldNamingMap[mappingKey] !== defaultLabel
            ) {
                return fieldNamingMap[mappingKey];
            }
            return defaultLabel;
        },
        [fieldNamingMap, defaultFieldNamingMap],
    );

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                user,
                companyConfig,
                setCompanyConfig,
                publicBrand,
                setPublicBrand,
                setUser,
                login,
                logout,
                reloadCompanyTheme,
                multitenantFlag,
                updateMultitenantFlag,
                companyCode,
                setIsLoading,
                adminView,
                setAdminView,
                allowAdditionalUser,
                isSelfRegistrationEnabled,
                systemConfig,
                clientTierMeetsRequirement,
                hasAttribute,
                hasSecurityAttribute,
                fieldNamingMap,
                defaultFieldNamingMap,
                enableEmailAndPhoneValidation,
                getFieldLabel,
                reloadFieldNaming,
                menuLabels,
                displayIndividualAssetMenu,
                menuLabelsLoaded,
                reloadMenuLabels,
            }}
        >
            {isLoading ? (
                <Spinner
                    width={50}
                    className={'center-horizontal-and-vertical'}
                />
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthContextProvider, useAuthContext };
